import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { FeedItem } from '@algoboo/schema/api'
import { evidenceId } from '@algoboo/schema/canonical'
import { EvidenceDrawer, type EvidenceVerification } from '@algoboo/ui'
import { errorCopy, isApiError, useEvidence } from '../lib/api'
import type { EvidenceObject } from '@algoboo/schema/api'

type Open = (id: string, item?: FeedItem) => void
const Ctx = createContext<Open>(() => {})

/** Recompute the content hash in the browser from the served record (a record it cannot hash stays unchecked). */
function verify(r: EvidenceObject, id: string): EvidenceVerification {
  try {
    return evidenceId({
      objectType: r.objectType,
      schemaVersion: r.schemaVersion,
      actorKind: r.actorKind,
      actorId: r.actorId,
      agentId: r.agentId,
      idempotencyKey: r.idempotencyKey,
      sourceObjectIds: r.sourceObjectIds,
      payload: r.payload,
    }) === id
      ? 'verified'
      : 'mismatch'
  } catch {
    return 'unchecked'
  }
}

/** `openEvidence(id, item?)` from anywhere: feed items, decisions, trades. */
export function useOpenEvidence(): Open {
  return useContext(Ctx)
}

export function EvidenceHost({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<string[]>([])
  const [item, setItem] = useState<FeedItem | null>(null)
  const id = stack[stack.length - 1] ?? null
  const q = useEvidence(id)
  const open = useCallback<Open>((i, it) => {
    setStack([i])
    setItem(it ?? null)
  }, [])
  const verification: EvidenceVerification = useMemo(() => {
    if (!q.data || !id) return 'unchecked'
    return verify(q.data, id)
  }, [q.data, id])
  const err = q.error ? (isApiError(q.error) && q.error.code === 'NOT_FOUND' ? 'No evidence record with this id. The id above is still the content hash of the record.' : isApiError(q.error) ? errorCopy(q.error.code).body : 'Could not load the record.') : undefined
  return (
    <Ctx.Provider value={open}>
      {children}
      <EvidenceDrawer
        open={id !== null}
        onClose={() => setStack([])}
        id={id}
        evidence={q.data}
        loading={q.isLoading}
        error={err}
        item={stack.length === 1 ? item : null}
        verification={verification}
        onOpenSource={(s) => setStack((st) => [...st, s])}
        history={stack.length - 1}
        onBack={() => setStack((st) => st.slice(0, -1))}
      />
    </Ctx.Provider>
  )
}
